import { useMemo, useState } from 'react';
import { DiffEditor } from '@monaco-editor/react';
import { Modal, Segmented, Tag, Typography } from 'antd';
import type { HistoryEntry } from '@core/entities/History';
import { useThemeStore } from '@presentation/stores/themeStore';

interface ResponseDiffModalProps {
  open: boolean;
  onClose: () => void;
  left: HistoryEntry | null;
  right: HistoryEntry | null;
}

const detectLanguage = (headers: Record<string, string[]> | null | undefined): string => {
  const contentType = headers?.['Content-Type']?.[0] ?? headers?.['content-type']?.[0] ?? '';
  if (contentType.includes('json')) return 'json';
  if (contentType.includes('xml')) return 'xml';
  if (contentType.includes('html')) return 'html';
  return 'plaintext';
};

const tryFormat = (body: string, language: string): string => {
  if (language !== 'json') return body;
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

const statusColor = (statusCode: number | null): string => {
  if (statusCode === null) return 'default';
  if (statusCode < 300) return 'success';
  if (statusCode < 400) return 'processing';
  if (statusCode < 500) return 'warning';
  return 'error';
};

const EntrySummary = ({ entry }: { entry: HistoryEntry | null }) => {
  if (!entry) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Tag color={statusColor(entry.statusCode)} className="shrink-0">
        {entry.statusCode ?? '—'}
      </Tag>
      <Typography.Text className="truncate text-xs" title={entry.url}>
        {entry.url}
      </Typography.Text>
      <span className="shrink-0 text-xs text-gray-400">
        {new Date(entry.executedAt).toLocaleTimeString()}
      </span>
    </div>
  );
};

export const ResponseDiffModal = ({ open, onClose, left, right }: ResponseDiffModalProps) => {
  const themeMode = useThemeStore((state) => state.mode);
  const [mode, setMode] = useState<'side-by-side' | 'inline'>('side-by-side');

  const leftLanguage = detectLanguage(left?.response?.headers);
  const rightLanguage = detectLanguage(right?.response?.headers);
  const language = leftLanguage === rightLanguage ? leftLanguage : 'plaintext';

  const original = useMemo(
    () => (left?.response?.body ? tryFormat(left.response.body, leftLanguage) : ''),
    [left, leftLanguage],
  );
  const modified = useMemo(
    () => (right?.response?.body ? tryFormat(right.response.body, rightLanguage) : ''),
    [right, rightLanguage],
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      title="Compare responses"
      destroyOnHidden
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-4">
            <EntrySummary entry={left} />
            <EntrySummary entry={right} />
          </div>
          <Segmented
            size="small"
            value={mode}
            onChange={(value) => setMode(value as 'side-by-side' | 'inline')}
            options={[
              { label: 'Side by side', value: 'side-by-side' },
              { label: 'Inline', value: 'inline' },
            ]}
          />
        </div>

        <div className="h-[520px] overflow-hidden rounded border border-black/10 dark:border-white/10">
          <DiffEditor
            height="100%"
            language={language}
            theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
            original={original}
            modified={modified}
            options={{
              readOnly: true,
              renderSideBySide: mode === 'side-by-side',
              minimap: { enabled: false },
              fontSize: 13,
              scrollBeyondLastLine: false,
            }}
          />
        </div>
      </div>
    </Modal>
  );
};
